// Based on cube-js/cube CubeSQLConverter:
// https://github.com/cube-js/cube/blob/2e019d4e0ead7b5c990f97f2ccaa45483e1b4b8a/packages/cubejs-playground/src/QueryBuilderV2/utils/cube-sql-converter.ts
import type { CubeMetaDefinition } from 'shared/src/api/cube-api-types';
import type { CubeQuerySchema } from 'shared/src/api/cube-query-schema';
import { parseDateRange } from './cube-date-parser';

const MemberKind = { Measure: 0, Dimension: 1, Segment: 2, TimeDimension: 3 } as const;
type MemberKind = (typeof MemberKind)[keyof typeof MemberKind];

type ResolvedMember = {
  name: string;
  kind: MemberKind;
  granularity?: string;
};

type LeafFilter = {
  member: string;
  operator: string;
  values?: string[];
};

type InternalFilter = LeafFilter | { and: InternalFilter[] } | { or: InternalFilter[] };

type CubeSqlOptions = {
  timezone?: string;
  now?: Date;
};

/**
 * Convert a Cube query + meta@data into a Cube SQL API query string.
 */
export function cubeToCubeSql(
  query: CubeQuerySchema,
  meta: Record<string, CubeMetaDefinition>,
  options?: CubeSqlOptions
): string {
  const pathToKind = buildPathToKind(meta);
  const { members, filters, tables } = decompose(query, options);
  return buildSql(query, members, filters, tables, pathToKind);
}

// ─── Metadata Indexing ───

function buildPathToKind(meta: Record<string, CubeMetaDefinition>): Record<string, MemberKind> {
  const map: Record<string, MemberKind> = {};
  for (const cube of Object.values(meta)) {
    for (const [ref, dim] of Object.entries(cube.dimensions)) {
      map[ref] = dim.type === 'time' ? MemberKind.TimeDimension : MemberKind.Dimension;
    }
    for (const ref of Object.keys(cube.measures)) {
      map[ref] = MemberKind.Measure;
    }
  }
  return map;
}

function getPathKind(path: string, pathToKind: Record<string, MemberKind>): MemberKind {
  if (!path || pathToKind[path] === undefined) {
    throw new Error(`Member '${path}' not found in metadata`);
  }
  return pathToKind[path];
}

// ─── Query Decomposition ───

function decompose(
  query: CubeQuerySchema,
  options?: CubeSqlOptions
): {
  members: ResolvedMember[];
  filters: InternalFilter[];
  tables: string[];
} {
  const members: ResolvedMember[] = [];
  const filters: InternalFilter[] = [];

  for (const name of query.dimensions ?? []) {
    members.push({ name, kind: MemberKind.Dimension });
  }

  for (const td of query.timeDimensions ?? []) {
    if (td.dateRange) {
      let range: [string, string];
      if (Array.isArray(td.dateRange)) {
        range = [td.dateRange[0], td.dateRange[1]];
      } else {
        const timezone = options?.timezone ?? 'UTC';
        range = parseDateRange(td.dateRange, timezone, options?.now);
      }
      filters.push({ member: td.dimension, operator: 'inDateRange', values: range });
    }
    members.push({
      name: td.dimension,
      kind: MemberKind.TimeDimension,
      granularity: td.granularity
    });
  }

  for (const name of query.measures ?? []) {
    members.push({ name, kind: MemberKind.Measure });
  }

  for (const f of query.filters ?? []) {
    filters.push(f as InternalFilter);
  }

  const allPaths = members.map(m => m.name);
  for (const o of query.order ?? []) {
    allPaths.push(o[0]);
  }
  const tables = Array.from(new Set(allPaths.map(p => p.split('.')[0])));

  return { members, filters, tables };
}

// ─── SQL Generation ───

function buildSql(
  query: CubeQuerySchema,
  members: ResolvedMember[],
  filters: InternalFilter[],
  tables: string[],
  pathToKind: Record<string, MemberKind>
): string {
  const lines: string[] = [];
  const groupBy: string[] = [];

  const selectItems = members.map((member, index) => {
    if (member.kind !== MemberKind.Measure) {
      groupBy.push(String(index + 1));
    }

    if (member.kind === MemberKind.Measure) return `MEASURE(${member.name})`;
    if (member.kind === MemberKind.TimeDimension && member.granularity) {
      return `DATE_TRUNC('${member.granularity}', ${member.name})`;
    }
    return member.name;
  });

  lines.push('SELECT');
  lines.push(selectItems.map(item => `  ${item}`).join(',\n'));
  lines.push(`FROM ${tables[0]}`);

  for (const t of tables.slice(1)) {
    lines.push(`CROSS JOIN ${t}`);
  }

  // Classify filters by member kind in a single pass
  if (filters.length) {
    const measureFilters: InternalFilter[] = [];
    const allDimFilters: InternalFilter[] = [];

    for (const f of filters) {
      const paths = allFilterPaths(f);
      if (paths.every(p => getPathKind(p, pathToKind) === MemberKind.Measure)) {
        measureFilters.push(f);
      } else {
        allDimFilters.push(f);
      }
    }

    const where = flattenFilter({ and: allDimFilters }, false);
    if (where) lines.push(`WHERE ${where}`);

    if (groupBy.length) lines.push(`GROUP BY ${groupBy.join(', ')}`);

    if (measureFilters.length) {
      const having = flattenFilter({ and: measureFilters }, true);
      if (having) lines.push(`HAVING ${having}`);
    }
  } else {
    if (groupBy.length) lines.push(`GROUP BY ${groupBy.join(', ')}`);
  }

  if (query.order?.length) {
    const parts = query.order
      .map(([member, direction]: [string, 'asc' | 'desc']) => {
        const idx = members.findIndex(x => x.name === member);
        if (idx === -1) return null;
        return `${idx + 1}${direction === 'desc' ? ' DESC' : ''}`;
      })
      .filter(Boolean);
    if (parts.length) lines.push(`ORDER BY ${parts.join(', ')}`);
  }

  if (query.limit) lines.push(`LIMIT ${query.limit}`);
  if (query.offset) lines.push(`OFFSET ${query.offset}`);

  return `${lines.join('\n')};`;
}

// ─── Filter Helpers ───

function allFilterPaths(filter: InternalFilter): string[] {
  if ('and' in filter) return filter.and.flatMap(allFilterPaths);
  if ('or' in filter) return filter.or.flatMap(allFilterPaths);
  return [filter.member];
}

function flattenFilter(filter: InternalFilter, isMeasure: boolean): string {
  if ('and' in filter) {
    const parts = filter.and.map(f => flattenFilter(f, isMeasure)).filter(Boolean);
    return parts.length ? `(${parts.join(' AND ')})` : '';
  }
  if ('or' in filter) {
    const parts = filter.or.map(f => flattenFilter(f, isMeasure)).filter(Boolean);
    return parts.length ? `(${parts.join(' OR ')})` : '';
  }
  const f: LeafFilter = isMeasure ? { ...filter, member: `MEASURE(${filter.member})` } : filter;
  return makeFilter(f);
}

const comparisonOperators: Record<string, string> = {
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<='
};

const operatorAliases: Record<string, string> = {
  afterDate: 'gt',
  afterOrOnDate: 'gte',
  beforeDate: 'lt',
  beforeOrOnDate: 'lte'
};

function esc(v: string): string {
  return `'${v.replace(/'/g, "''")}'`;
}

function repeatPattern(template: string, values: string[], negated: boolean): string {
  const parts = values.map(v => template.replace('{{v}}', v));
  return parts.length > 1 ? `(${parts.join(negated ? ' AND ' : ' OR ')})` : parts[0];
}

function makeFilter(filter: LeafFilter): string {
  if (operatorAliases[filter.operator]) {
    return makeFilter({ ...filter, operator: operatorAliases[filter.operator] });
  }

  if (filter.operator === 'set') return `${filter.member} IS NOT NULL`;
  if (filter.operator === 'notSet') return `${filter.member} IS NULL`;

  if (filter.operator === 'inDateRange') {
    return `${filter.member} >= ${esc(filter.values![0])} AND ${filter.member} <= ${esc(filter.values![1])}`;
  }
  if (filter.operator === 'notInDateRange') {
    return `${filter.member} < ${esc(filter.values![0])} OR ${filter.member} > ${esc(filter.values![1])}`;
  }

  if (filter.operator === 'equals' || filter.operator === 'notEquals') {
    const single = (filter.values?.length ?? 0) <= 1;
    const op = filter.operator === 'equals' ? (single ? '=' : 'IN') : single ? '!=' : 'NOT IN';
    const val = single ? esc(filter.values![0]) : `(${filter.values!.map(esc).join(', ')})`;
    const nullPart = filter.operator === 'notEquals' ? ` OR ${filter.member} IS NULL` : '';
    return `${filter.member} ${op} ${val}${nullPart}`;
  }

  if (filter.operator === 'contains')
    return repeatPattern(`${filter.member} ILIKE '%' || '{{v}}' || '%'`, filter.values!, false);
  if (filter.operator === 'notContains')
    return repeatPattern(`${filter.member} NOT ILIKE '%' || '{{v}}' || '%'`, filter.values!, true);
  if (filter.operator === 'startsWith')
    return repeatPattern(`starts_with(${filter.member}, '{{v}}')`, filter.values!, false);
  if (filter.operator === 'notStartsWith')
    return repeatPattern(`${filter.member} NOT LIKE '{{v}}%'`, filter.values!, true);
  if (filter.operator === 'endsWith')
    return repeatPattern(`ends_with(${filter.member}, '{{v}}')`, filter.values!, false);
  if (filter.operator === 'notEndsWith')
    return repeatPattern(`${filter.member} NOT LIKE '%{{v}}'`, filter.values!, true);

  if (filter.values && comparisonOperators[filter.operator]) {
    return `${filter.member} ${comparisonOperators[filter.operator]} ${esc(filter.values[0])}`;
  }

  throw new Error(`Unsupported filter operator: '${filter.operator}'`);
}
